import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  CreateRoomPayload,
  RoomBookedData,
  RoomDetailData,
  RoomListData,
  RoomStatusFilter,
  RoomTypeFilter,
  CreateRoomBookingPayload,
  RoomLayout,
  UpdateRoomLayoutPayload,
} from './model'
import { RoomAvailability } from '@/features/room/types'

export const RoomService = (client: AxiosInstance) => {
  return {
    async getRoomWithPagination(
      opt: PaginationOpt,
      type?: RoomTypeFilter,
      status?: RoomStatusFilter
    ): Promise<PaginationResponse<RoomListData>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: Record<string, unknown>[]; pagination: Record<string, unknown> }>(
          '/rooms',
          { params: { ...opt, type, status } }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, RoomListData)),
      }
    },
    async getRoomAvailability(params: {
      roomId?: string | number
      startTime?: string
      endTime?: string
      isAvailableOnly?: boolean
    }): Promise<RoomAvailability[]> {
      const { data } = await wrapApiCall(() =>
        client.get<RoomAvailability[]>('/rooms/availability', { params })
      )
      return data ?? []
    },
    async getRoomBookedWithPagination(
      opt: PaginationOpt,
      type?: RoomTypeFilter,
      status?: RoomStatusFilter
    ): Promise<PaginationResponse<RoomBookedData>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: Record<string, unknown>[]; pagination: Record<string, unknown> }>(
          '/room-bookings',
          { params: { ...opt, type, status } }
        )
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, RoomBookedData)),
      }
    },
    async getRoomDetail(id: string | number): Promise<RoomDetailData> {
      const { data } = await wrapApiCall(() => client.get<Record<string, unknown>>(`/rooms/${id}`))
      return deserialize(data, RoomDetailData)
    },
    async createRoom(payload: CreateRoomPayload): Promise<RoomListData> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/rooms', payload)
      )
      return deserialize(data, RoomDetailData)
    },
    async editRoom(id: string | number, payload: CreateRoomPayload): Promise<RoomListData> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/rooms/${id}`, payload)
      )
      return deserialize(data, RoomDetailData)
    },
    async deleteRoom(id: string | number): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() => client.delete<{ success: boolean }>(`/rooms/${id}`))
      return { success: data?.success }
    },
    async cancelRoomBooked(id: string | number): Promise<RoomListData> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/room-bookings/${id}/cancel`)
      )
      return deserialize(data, RoomDetailData)
    },
    /**
     * The room's seat map. A room that never had one still answers 200 with
     * `requiresSpotSelection: false` and an empty grid.
     */
    async getLayout(id: string | number): Promise<RoomLayout> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/rooms/${id}/layout`)
      )
      return deserialize(data, RoomLayout)
    },
    /**
     * Replaces the whole grid. 409 when a removed/changed seat is still held by
     * a future booking (retry with `force: true` to move those members off it),
     * or when the grid would leave fewer seats than the schedules already booked.
     */
    async updateLayout(id: string | number, payload: UpdateRoomLayoutPayload): Promise<RoomLayout> {
      const { data } = await wrapApiCall(() =>
        client.put<Record<string, unknown>>(`/rooms/${id}/layout`, payload)
      )
      return deserialize(data, RoomLayout)
    },
    async createRoomBooking(payload: CreateRoomBookingPayload) {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/room-bookings', payload)
      )
      return data
    },
  }
}
